import { useEffect, useState } from "react";
import { adminApi, formatApiError } from "../auth";
import { Eye, EyeOff, Trash2, ShieldCheck, Star } from "lucide-react";

const FILTERS = [
  { v: "",          label: "All" },
  { v: "published", label: "Published" },
  { v: "hidden",    label: "Hidden" },
];

const AdminReviews = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [err, setErr] = useState("");

  const load = () => {
    setLoading(true); setErr("");
    adminApi.get("/reviews", { params: filter ? { status: filter } : {} })
      .then((r) => setRows(r.data))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const toggle = async (r) => {
    const newStatus = r.status === "published" ? "hidden" : "published";
    try { await adminApi.patch(`/reviews/${r.id}/status`, { status: newStatus }); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const doDelete = async (id) => {
    if (!window.confirm("Delete this review? This re-aggregates the tyre's rating.")) return;
    try { await adminApi.delete(`/reviews/${id}`); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-reviews-page">
      <div className="flex items-center justify-between mb-1">
        <div>
          <h1 style={{ fontSize: 28 }}>Reviews</h1>
          <p className="vd-muted">Moderate customer reviews. Hiding or deleting auto-updates the tyre's star rating.</p>
        </div>
        <select className="vd-select" style={{ width: "auto" }} value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="reviews-filter">
          {FILTERS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
        </select>
      </div>

      {err && <p className="vd-card p-3 mt-4 text-sm" style={{ color: "var(--primary-red)" }} data-testid="reviews-error">{err}</p>}

      <div className="vd-card overflow-x-auto mt-5" data-testid="reviews-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left vd-muted uppercase text-xs tracking-wider border-b" style={{ borderColor: "var(--border-color)" }}>
              <th className="px-4 py-3">Product</th>
              <th>Reviewer</th>
              <th>Rating</th>
              <th>Comment</th>
              <th>Status</th>
              <th>Date</th>
              <th className="text-right pr-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="px-4 py-10 text-center vd-muted">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="7" className="px-4 py-10 text-center vd-muted">No reviews{filter ? ` with status “${filter}”` : ""}.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t" style={{ borderColor: "var(--border-color)" }} data-testid={`review-row-${r.id}`}>
                <td className="px-4 py-3 vd-heading text-xs max-w-[220px] truncate" title={r.product}>{r.product || "—"}</td>
                <td>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="vd-heading font-medium">{r.name}</span>
                    {r.verified && <span className="inline-flex items-center gap-1 text-[10px] font-bold rounded px-1.5 py-0.5" style={{ background: "rgba(0,180,80,0.15)", color: "#00A35C" }}><ShieldCheck size={10} /> Verified</span>}
                  </div>
                  {r.email && <p className="vd-muted text-xs">{r.email}</p>}
                </td>
                <td>
                  <div className="inline-flex items-center gap-0.5">
                    {[1,2,3,4,5].map((n) => <Star key={n} size={12} fill={n <= r.rating ? "var(--primary-red)" : "transparent"} color="var(--primary-red)" />)}
                  </div>
                </td>
                <td className="vd-muted text-xs max-w-[300px]"><p className="truncate" title={r.comment}>{r.title ? <strong className="vd-heading mr-1">{r.title}</strong> : null}{r.comment}</p></td>
                <td>
                  <span className="text-xs font-semibold rounded px-2 py-0.5" style={{ background: r.status === "published" ? "rgba(0,180,80,0.15)" : "rgba(255,179,11,0.18)", color: r.status === "published" ? "#00A35C" : "#B57E00" }} data-testid={`review-status-${r.id}`}>
                    {r.status}
                  </span>
                </td>
                <td className="vd-muted text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="pr-4">
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => toggle(r)} className="p-2 rounded hover:bg-[var(--bg-secondary)]" title={r.status === "published" ? "Hide" : "Publish"} data-testid={`review-toggle-${r.id}`}>
                      {r.status === "published" ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button onClick={() => doDelete(r.id)} className="p-2 rounded hover:bg-[var(--bg-secondary)]" title="Delete" data-testid={`review-delete-${r.id}`}>
                      <Trash2 size={14} color="var(--primary-red)" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminReviews;
